/**
 * Client-only onboarding / tour persistence. Read/write only from client components
 * (e.g. useEffect, click handlers) — never during SSR render.
 */

export type OnboardingIntent = "sell" | "buy" | "browse";
export type OnboardingAudience = "guest" | "auth";
export type OnboardingOpenSource =
  | "homepage_autoshow"
  | "floating_help"
  | "profile_tab";

export const ONBOARDING_STORAGE_KEYS = {
  homepageAutoshowDone: "sio_onboarding_homepage_autoshow_done",
  guestHomepageAutoshowDone: "sio_onboarding_guest_homepage_autoshow_done",
  intent: "sio_onboarding_intent",
  sellPreformDone: "sio_onboarding_sell_preform_done",
  marketIntroDone: "sio_onboarding_market_intro_done",
  myItemsStatusModalDone: "sio_onboarding_myitems_status_modal_done",
} as const;

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

export function readOnboardingFlag(key: string): boolean {
  if (!canUseStorage()) return false;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function writeOnboardingFlag(key: string, value: boolean): void {
  if (!canUseStorage()) return;
  try {
    if (value) {
      localStorage.setItem(key, "1");
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

export function readOnboardingIntent(): OnboardingIntent | null {
  if (!canUseStorage()) return null;
  try {
    const v = localStorage.getItem(ONBOARDING_STORAGE_KEYS.intent);
    if (v === "sell" || v === "buy" || v === "browse") return v;
    return null;
  } catch {
    return null;
  }
}

export function writeOnboardingIntent(intent: OnboardingIntent): void {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEYS.intent, intent);
  } catch {
    /* ignore */
  }
}
